import type { components, operations } from '@customerio/hydra-ui-api';
// Source module import (not the barrel) to avoid a pre-auth import cycle.
import { hydraUiApi } from '@/services/api/hydra-ui-api';
import { getGaClientId } from '@/utils/ga-client-id';
import { getReferrerRequestParams } from './attribution';
import { executeRecaptcha } from './recaptcha';

// Pre-auth signup API.
type Schemas = components['schemas'];

export type SignupPrepopulateData = {
  companyName: string;
  role: string;
};

type SignupStartQuery = NonNullable<operations['get-v1-account_signup-start']['parameters']['query']>;

/**
 * Kick off a signup: email vetting, verification-code send, marketing
 * identify. The reCAPTCHA token rides as a query param — the server never
 * reads it from the body.
 */
export async function startSignup({
  email,
  isGoogleSignup,
  signupType,
}: {
  email: string;
  isGoogleSignup?: boolean;
  signupType?: string;
}): Promise<SignupPrepopulateData> {
  const recaptchaToken = await executeRecaptcha('signup');

  const query: SignupStartQuery = {
    email,
    skip_verification: isGoogleSignup ?? false,
  };
  if (recaptchaToken != null && recaptchaToken !== '') {
    query['g-recaptcha-response'] = recaptchaToken;
  }
  if (signupType != null && signupType !== '') {
    query.signup_type = signupType;
  }

  const response = await hydraUiApi.accountSignup.getStart({ params: { query } });

  return {
    companyName: response.signup_prepopulate_data.company_name,
    role: response.signup_prepopulate_data.role,
  };
}

/**
 * The `signup` request schema; a present `promo_code` routes to the promo
 * endpoint.
 */
export type RegisterAccountRequest = Schemas['services_ui_api_accounts_signupRequest2'] & {
  meta: NonNullable<Schemas['services_ui_api_accounts_signupRequest2']['meta']>;
  promo_code?: string;
};

/**
 * The startup-program fields. `submitStartupApplication` reports them to
 * Marketing and persists nothing, so they must ALSO ride on the register call:
 * `meta.startup` is what lands in `account_meta`, which is where Flightdeck and
 * the Trusty review pipeline read a startup signup's context from.
 */
export type StartupSignupFields = Schemas['services_domains_accounts_StartupSignupFields'];

export type RegisterAccountResponse = {
  account: Schemas['services_ui_api_model_Account'];
  user: Schemas['services_ui_api_model_User'];
};

export type BuildRegisterAccountBodyArgs = {
  values: {
    email: string;
    firstName: string;
    lastName: string;
    password: string;
    companyName: string;
    companyUrl: string;
    dataCenter: string;
    acceptTerms: boolean;
    signupType: string;
    isGoogleSignup: boolean | undefined;
    noWebsite: boolean;
  };
  messagingUseCases: string[];
  optInChannels: string[];
  emailVolume: string | null;
  role: string | null;
  messageChannels: string[];
  promoCode?: string;
  // Startup signups only; the flow engine maps the form values at register time.
  startupFields?: StartupSignupFields;
};

/**
 * The register payload, matching `registerWithVerification`. Pure so tests
 * can assert the full shape.
 */
export function buildRegisterAccountBody({
  values,
  messagingUseCases,
  optInChannels,
  emailVolume,
  role,
  messageChannels,
  promoCode,
  startupFields,
}: BuildRegisterAccountBodyArgs): RegisterAccountRequest {
  const websiteUrl = values.noWebsite ? '' : values.companyUrl;
  const hasPromoCode = promoCode != null && promoCode !== '';
  // An object with nothing in it would still persist a blob, which reads
  // downstream as a startup signup whose every field is blank. Unset and
  // whitespace-only values count as nothing, not as an answer.
  const hasStartupFields =
    startupFields != null && Object.values(startupFields).some((v) => typeof v === 'string' && v.trim() !== '');

  return {
    email: values.email,
    password: values.password,
    first_name: values.firstName,
    last_name: values.lastName,
    data_center: values.dataCenter,
    company_name: values.companyName,
    website_url: websiteUrl,
    messaging_use_cases: messagingUseCases,
    opt_in_channels: optInChannels,
    monthly_email_volume: emailVolume ?? '',
    role: role ?? '',
    channels: messageChannels,
    meta: {
      utc_offset_minutes: -1 * new Date().getTimezoneOffset(),
      signup_accepted_terms: values.acceptTerms,
      signup_type: values.signupType,
      is_google_signup: values.isGoogleSignup,
      signup_attrs: {
        form_source: 'new-cio-account',
        campaign_info: null,
      },
      cookies: getReferrerRequestParams({ includeSnowplow: true }),
      ...(hasStartupFields ? { startup: startupFields } : {}),
    },
    ...(hasPromoCode ? { promo_code: promoCode } : {}),
  };
}

/**
 * Create the account. A `promo_code` routes to the promo endpoint (Ordway
 * billing). On success the server sets the session cookie; the caller flips
 * the local session with the `AccountCreation` authenticator.
 */
export async function registerAccount(body: RegisterAccountRequest): Promise<RegisterAccountResponse> {
  const recaptchaToken = await executeRecaptcha('signup_signup');
  const query: NonNullable<operations['post-v1-account_signup-signup']['parameters']['query']> = {};
  if (recaptchaToken != null && recaptchaToken !== '') {
    query['g-recaptcha-response'] = recaptchaToken;
  }

  const promoCode = body.promo_code;
  const response =
    promoCode != null && promoCode !== ''
      ? await hydraUiApi.accountSignup.promo({ params: { query }, body: { ...body, promo_code: promoCode } })
      : await hydraUiApi.accountSignup.signup({ params: { query }, body });

  const { account, user } = response;
  if (account == null || user == null) {
    throw new Error('signup response missing account or user');
  }
  return { account, user };
}

type TrackIdentifyRequest = Schemas['services_ui_api_accounts_signupTrackIdentifyRequest'];

/** The wire traits — exactly the nine fields the server forwards to the marketing CDP. */
export type SignupIdentifyTraits = TrackIdentifyRequest['traits'];

/**
 * Only these reach Segment — the server discards any other trait at
 * unmarshal.
 */
export type SignupIdentifyAttrs = {
  email: string;
  firstName?: string;
  lastName?: string;
  companyName?: string;
  useCase?: string;
  useCaseDetail?: string;
  numberOfContacts?: string;
  workPhone?: string;
  acceptTerms?: boolean;
};

// Unset attributes become '' — the server drops empty-string traits.
export function buildIdentifyTraits(attrs: SignupIdentifyAttrs): SignupIdentifyTraits {
  return {
    email: attrs.email,
    first_name: attrs.firstName ?? '',
    last_name: attrs.lastName ?? '',
    company_name: attrs.companyName ?? '',
    use_case: attrs.useCase ?? '',
    use_case_detail: attrs.useCaseDetail ?? '',
    number_of_contacts: attrs.numberOfContacts ?? '',
    work_phone: attrs.workPhone ?? '',
    accept_terms: attrs.acceptTerms === undefined ? '' : String(attrs.acceptTerms),
  };
}

/** Marketing identify keyed on email. Fire-and-forget; never blocks the funnel. */
export function trackIdentify(attrs: SignupIdentifyAttrs): void {
  void hydraUiApi.accountSignup
    .trackIdentify({ body: { email: attrs.email, traits: buildIdentifyTraits(attrs) } })
    .catch(() => {});
}

export type PromoCode = Schemas['services_ui_api_model_PromoCode'];

/**
 * Validate a promo code from the signup URL. The server 404s for unknown *and*
 * already-redeemed codes alike (anti-leak), so any failure resolves `null` —
 * an invalid promo never blocks signup.
 */
export async function getPromoCode(promoCode: string): Promise<PromoCode | null> {
  try {
    return await hydraUiApi.accountSignup.getPromoPromoCodes({
      params: { path: { promo_code_id: promoCode } },
    });
  } catch {
    return null;
  }
}

/** Confirm an account from an emailed confirmation code. */
export async function confirmAccount(confirmationCode: string): Promise<void> {
  await hydraUiApi.accountSignup.confirmAccount({
    body: { confirmation_code: confirmationCode },
  });
}

/**
 * A confirmed code can carry a one-shot join token, minted when a paid account
 * already exists on the verified email's domain. Both fields are absent on the
 * ordinary path, so normalize before testing the token.
 */
export type VerificationCodeResult = Schemas['services_ui_api_accounts_signupCodeVerificationResponse'];

/**
 * Check an emailed verification code. Rejects on every non-2xx so the verify
 * step can branch on `error.status` for messaging and analytics.
 */
export async function validateVerificationCode(
  email: string,
  verificationCode: string,
): Promise<VerificationCodeResult> {
  return await hydraUiApi.accountSignup.validate({
    body: { email, verification_code: verificationCode },
  });
}

/** Re-send the verification code; `signup_type` routes the mail sender. */
export async function sendVerification(email: string, signupType?: string): Promise<void> {
  await hydraUiApi.accountSignup.sendVerification({
    body: { email, ...(signupType != null && signupType !== '' ? { signup_type: signupType } : {}) },
  });
}

type StartupApplicationRequest = Schemas['services_ui_api_accounts_startupApplicationRequest'];

export type SubmitStartupApplicationArgs = Omit<
  StartupApplicationRequest,
  'signup_type' | 'url_params' | 'referrer' | 'ga_client_id' | 'cio_anonymous_id' | 'referral_id'
> & { referral_id?: string };

/**
 * Pre-account startup-program application. The attribution fields are
 * appended here, not by the caller, so the step UI can't drop them.
 */
export async function submitStartupApplication(
  args: SubmitStartupApplicationArgs,
  { landingUrlParams, anonymousId }: { landingUrlParams: string; anonymousId: string },
): Promise<void> {
  const body: StartupApplicationRequest = {
    ...args,
    referral_id: args.referral_id ?? '',
    signup_type: 'startup',
    url_params: landingUrlParams,
    referrer: document.referrer,
    ga_client_id: getGaClientId(),
    cio_anonymous_id: anonymousId,
  };

  await hydraUiApi.accountSignup.startupApplication({ body });
}
